import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from '@stripe/react-connect-js';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Notification from '../../components/notification/notification';
import { TWButton } from '../../components/ui';
import connectApi from '../../services/connectApi';
import { groupRequirements } from './connect-requirements';
import { onboardingCollectionOptions } from './connect-prefilled-fields';
import useConnectInstance, {
  ACCOUNT_ONBOARDING_COMPONENTS,
} from './use-connect-instance';

// Everything Stripe still wants from this account, flattened out of the
// requirement module's groups (overdue first, labels de-duplicated). The
// restaurant no longer sees it as a list (owner, 2026-09-25: the page is
// Stripe's form); it is read, not drawn. An empty list is one condition of
// the "nothing outstanding" test below, which picks the quiet "Update your
// details on Stripe" link over the Continue on Stripe card and keeps the
// automatic mint from opening the form on a finished account. Each item
// keeps its group key.
export const stillToDoItems = (status) => {
  const groups = groupRequirements(status?.requirements) || [];
  return groups.reduce(
    (items, group) =>
      items.concat(
        (group?.items || []).map((item) => ({ ...item, group: group.key })),
      ),
    [],
  );
};

// The restaurant-facing half of Stripe onboarding. A component, not a page:
// SC-R8-DASH-01 mounts it inside the company-create flow, so it owns the
// explanation, the way into Stripe and the Stripe mount, and the page around
// it owns only the heading.
//
// `onStatusChange` is optional and fires only from inside `loadStatus`
// below, with whatever it read — the mount effect's own first call, the
// embedded widget's exit (`onExitOnboarding` calls `loadStatus` directly),
// and the visibility/focus listener firing once the owner returns from
// Stripe (it also just calls `loadStatus`). It does NOT fire from either
// link-minting action further down (`onClickContinueOnStripe`,
// `onClickHavingTrouble`): both adopt the response by calling `setStatus`
// directly, never going through `loadStatus`, so a caller relying on this
// callback alone may hold a slightly stale `hasAccount`/`connectStatus` flag
// — from before the link was minted — until the next time `loadStatus` runs.
// Settings -> Payments (account-sections.js) is the one caller that supplies
// it: that page reads its own copy of this same status once, up front, to
// decide whether to mount this component at all, and had no way to learn
// when onboarding finished in place — a restaurant who completed setup
// without leaving the page watched this component empty out while the page
// kept rendering it, with no account management, no Bank heading and no
// notification banner until a reload. The company-create mount site has no
// such page-level copy to reconcile, so it passes nothing and this stays a
// no-op there.
//
// Must be a stable reference (e.g. wrapped in the caller's own useCallback)
// the same way use-connect-instance.js requires of the `fetchSecret` it is
// handed: this callback sits in `loadStatus`'s own dependency array, and
// `loadStatus` is the mount effect's only dependency below, so an inline
// arrow passed here would re-run that effect, and re-read status, on every
// single render. payments.js, the one caller that supplies it, already
// passes a `useCallback` for this reason.
const ConnectOnboarding = ({ companyId, onStatusChange }) => {
  const isDark = useSelector((state) => state.commonReducer.isDark);

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [linkLoading, setLinkLoading] = useState(false);
  // Set only when a *background* refresh fails after a first read already
  // succeeded — never on the first load, which has its own persistent
  // `statusUnknown` banner below. Without this, `onExitOnboarding` and the
  // visibility/focus listener can fail silently forever: `status` stays
  // truthy from the earlier success, so neither that banner nor a toast ever
  // fires, and the restaurant is left with no sign anything is out of date.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  // GetStatus and Refresh always pass onboardingUrl/clientSecret as null
  // (connectApi's toConnectStatus adapter, mirroring
  // ConnectAccountService.ToDtoAsync) — only StartOnboarding and ResendLink
  // ever mint either, and each mint is a single-use Stripe Account Link plus
  // Account Session, so nothing can be handed to useConnectInstance until the
  // owner has clicked Continue on Stripe at least once this visit. The secret
  // lives here, in the component, rather than inside the hook:
  // onClickContinueOnStripe below is already the single place that decides
  // resendLink vs. startOnboarding and already needs that same response's
  // onboardingUrl for the Account Link fallback, so one fetch answers both
  // questions instead of the hook mounting a second, competing call. The hook
  // is left a pure factory — secret in, Stripe instance out — with no
  // knowledge of companyId, hasAccount, or either endpoint.
  const [clientSecret, setClientSecret] = useState(null);
  const { instance, error: instanceError } = useConnectInstance(
    clientSecret,
    ACCOUNT_ONBOARDING_COMPONENTS,
    isDark,
  );

  // Armed only once we have sent the owner to Stripe's hosted page, so the
  // return trip re-reads status without refetching on every tab switch.
  const awaitingReturn = useRef(false);
  // The automatic mint below fires at most once per mount. Without this a
  // failed mint would be retried on every render, and each attempt burns a
  // single-use Account Link.
  const autoMinted = useRef(false);
  // Written at the bottom of render, read by the automatic mint effect.
  const setupDoneRef = useRef(false);
  // Two getStatus calls for two companyIds can land out of order; only the
  // newest request may write state.
  const requestId = useRef(0);
  // True once a status read has ever resolved to a real object. Tracked in a
  // ref rather than by reading `status` from closure so `loadStatus` need not
  // depend on it — `status` is never reset to null once set, so this ref and
  // "a background refresh, not the first load" mean the same thing.
  const hasLoadedRef = useRef(false);
  // Matches the `active` flag pattern in use-connect-instance.js: guards
  // every setState issued after an await (loadStatus, onClickContinueOnStripe)
  // so an unmounted component never writes state.
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const loadStatus = useCallback(async () => {
    if (!companyId) {
      setStatusLoading(false);
      return;
    }
    requestId.current += 1;
    const id = requestId.current;
    setStatusLoading(true);
    try {
      const res = await connectApi.getStatus(companyId);
      if (id !== requestId.current || !isMounted.current) return;
      const next = connectApi.toConnectStatus(res);
      setStatus(next);
      onStatusChange?.(next);
      setRefreshFailed(false);
      if (next) hasLoadedRef.current = true;
    } catch {
      if (id !== requestId.current || !isMounted.current) return;
      // `status` is left exactly as it was — null on a first load, or the
      // last confirmed value on a later refresh — never guessed at. That is
      // what lets `statusUnknown` below tell "we don't know" apart from "we
      // read it and there is no account", so the account-creating branch in
      // onClickContinueOnStripe cannot mistake one for the other. No toast
      // here on purpose: a toast that auto-dismisses in five seconds was the
      // whole problem, and stacking one on top of the persistent "couldn't
      // check" retry below would read as two alarms for one calm fact.
      //
      // A *background* refresh failing is a different fact from a first load
      // failing: `statusUnknown` stays false (status is still the last good
      // read), so this is the only signal that the list on screen may be
      // stale. hasLoadedRef guards it so a first-load failure — already
      // covered by statusUnknown — does not also trip this quieter one.
      if (hasLoadedRef.current) setRefreshFailed(true);
    } finally {
      if (id === requestId.current && isMounted.current) setStatusLoading(false);
    }
  }, [companyId, onStatusChange]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Stripe's widget only knows what it collected in this session. Our own
  // GET /Connect/Status is the source of truth for what is left, so both the
  // embedded completion callback and the return from the link path re-read it.
  const onExitOnboarding = useCallback(() => {
    awaitingReturn.current = false;
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const onReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (!awaitingReturn.current) return;
      awaitingReturn.current = false;
      loadStatus();
    };
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('focus', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('focus', onReturn);
    };
  }, [loadStatus]);

  // Mints a fresh single-use Account Link + Account Session and adopts the
  // status it comes back with. Unlike the spec this screen was first built
  // against, StartOnboarding and ResendLink return the same
  // ConnectAccountStatusDto shape GetStatus does — just with onboardingUrl
  // and clientSecret populated alongside it — so the result is a complete
  // status, not a delta, and fully replaces `status` rather than patching it.
  const mintLink = useCallback(async () => {
    // The link always comes from our API. The browser never supplies a
    // Stripe account id, and nothing here ever points at dashboard.stripe.com.
    const res = status?.hasAccount
      ? await connectApi.resendLink(companyId)
      : await connectApi.startOnboarding(companyId);
    const next = connectApi.toConnectStatus(res);
    if (!next) {
      throw new Error(
        'Stripe did not return an onboarding link. Please try again.',
      );
    }
    return next;
  }, [companyId, status]);

  const openStripeWindow = (next) => {
    if (!next.onboardingUrl) {
      throw new Error(
        'Stripe did not return an onboarding link. Please try again.',
      );
    }
    awaitingReturn.current = true;
    const opened = window.open(
      next.onboardingUrl,
      '_blank',
      'noopener,noreferrer',
    );
    if (!opened) {
      // Pop-up blocked. The link is a one-shot Account Link, so there is
      // nothing useful to echo — say what to change and let them retry.
      awaitingReturn.current = false;
      throw new Error(
        'Your browser blocked the Stripe window. Allow pop-ups for this site and try again.',
      );
    }
  };

  // The primary action, before anything has been minted this visit. Prefers
  // mounting Stripe's embedded widget in place when a publishable key is
  // configured and the mint actually returned a client secret; otherwise —
  // no key configured, or a secret didn't come back — falls back to opening
  // the Account Link in a new window, same as when there is no key at all.
  const onClickContinueOnStripe = async () => {
    // Never post StartOnboarding on a guess: until getStatus has answered we
    // do not know whether this company already has an account, and
    // StartOnboarding creates one. `!status` also catches a *failed* read
    // (see `statusUnknown`) — a failure is "we don't know", never "confirmed
    // no account", so it must be retried rather than guessed past. The
    // button is disabled for the same condition; this is the backstop.
    if (statusLoading || linkLoading || !status) return;
    setLinkLoading(true);
    try {
      const next = await mintLink();
      if (isMounted.current) setStatus(next);
      const publishableKey = (
        process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
      ).trim();
      if (publishableKey && next.clientSecret) {
        // Stay on this page: handing the secret to useConnectInstance mounts
        // the embedded widget below on the next render. No window is opened
        // and no Account Link is consumed.
        if (isMounted.current) setClientSecret(next.clientSecret);
        return;
      }
      openStripeWindow(next);
    } catch (error) {
      awaitingReturn.current = false;
      if (isMounted.current) {
        setNotf({ display: true, type: 'error', message: error });
      }
    } finally {
      if (isMounted.current) setLinkLoading(false);
    }
  };

  // Open the form, rather than a button that opens the form.
  //
  // Only when the account already exists. The press this replaces did two
  // different things depending on `hasAccount`: for a company with no account
  // it calls StartOnboarding, which CREATES one — and a Stripe account must
  // never be created just because somebody opened a page. With an account in
  // hand the call is ResendLink, which creates nothing and only mints a fresh
  // link and session, so it is safe to make on arrival.
  //
  // That is exactly the case the dashboard's "Finish verification" banner
  // sends people to: an account that exists with setup unfinished. It saves
  // the restaurant one of the two clicks that stood between the banner and
  // the form. The second — Stripe's own "Add information" — belongs to
  // Stripe's component and has no documented way to skip it.
  useEffect(() => {
    if (autoMinted.current) return;
    if (!status?.hasAccount) return;
    if (statusLoading || linkLoading || clientSecret) return;
    if (setupDoneRef.current) return;
    const publishableKey = (
      process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
    ).trim();
    // No key means the embedded widget can never mount, and the only path is
    // the Account Link in a new window — which a browser blocks unless a
    // person clicked for it. Leave the button alone in that case.
    if (!publishableKey) return;
    autoMinted.current = true;
    (async () => {
      setLinkLoading(true);
      try {
        const next = await mintLink();
        if (!isMounted.current) return;
        setStatus(next);
        if (next.clientSecret) setClientSecret(next.clientSecret);
      } catch {
        // Silent on purpose: nobody asked for this, so nobody should be told
        // it failed. The button below is still there, still says what it
        // does, and still reports its own errors when pressed.
      } finally {
        if (isMounted.current) setLinkLoading(false);
      }
    })();
  }, [status, statusLoading, linkLoading, clientSecret, mintLink]);

  // The embedded widget's own escape hatch. Deliberately does not repeat the
  // branch above: the widget is already on screen and is the reason this
  // button exists, so trying to mount it again would not offer an
  // alternative. This always opens a fresh Account Link in a new window.
  const onClickHavingTrouble = async () => {
    if (statusLoading || linkLoading || !status) return;
    setLinkLoading(true);
    try {
      const next = await mintLink();
      if (isMounted.current) setStatus(next);
      openStripeWindow(next);
    } catch (error) {
      awaitingReturn.current = false;
      if (isMounted.current) {
        setNotf({ display: true, type: 'error', message: error });
      }
    } finally {
      if (isMounted.current) setLinkLoading(false);
    }
  };

  const items = stillToDoItems(status);
  // Distinct from "there is no account": true only once a status read has
  // finished and still left us with nothing (a first load that failed, since
  // a successful one always resolves `status` to an object). Never asserted
  // while a read is still in flight.
  const statusUnknown = !statusLoading && !status;
  // `stillToDoItems` returns [] for three different facts: Stripe confirmed
  // nothing is due, the payload carried no `requirements` object, and the
  // company has no Connect account at all. Only the first of those is "Stripe
  // has everything it needs" — mirrors connect-tab.js's identical
  // `nothingOutstandingConfirmed` guard, plus the `hasAccount` check that
  // guard has structurally for free (it only renders once `hasAccount` is
  // already true) and this screen must make explicit, since a restaurant can
  // land here before ever starting Stripe onboarding.
  const nothingOutstandingConfirmed =
    items.length === 0 &&
    !!status?.hasAccount &&
    (!!status?.requirements ||
      status?.connectStatus === 'READY' ||
      status?.connectStatus === 'PENDING');
  const showEmbedded = Boolean(instance) && !instanceError;
  // What Stripe should not draw, because the API already sent it — and only
  // what Stripe agrees is no longer outstanding, so a prefill that failed
  // leaves its field on the form rather than stranding the restaurant. See
  // connect-prefilled-fields.js.
  const collectionOptions = onboardingCollectionOptions(status?.requirements);

  // Named for what it means to the reader rather than to the guard above: an
  // account exists and the payload positively supports "Stripe wants nothing"
  // — the `hasAccount` half is already inside nothingOutstandingConfirmed, so
  // this is that same fact under the name the render below reads by.
  //
  // Everything below reads differently in this state. The screen was written
  // for a restaurant that has not started, and went on inviting one that had
  // already finished: a "takes about five minutes" call to action beside the
  // sentence saying Stripe needed nothing, and a primary button that mints a
  // fresh single-use Account Link on every press.
  const setupDone = nothingOutstandingConfirmed;
  // Read by the automatic mint above, which runs before this line on every
  // render. A ref rather than a dependency: the effect must not re-run when
  // the flag flips, only consult it at the moment it fires.
  setupDoneRef.current = setupDone;

  const linkPath = setupDone ? (
    // Finished. No border, no primary button: the page should read as done,
    // and the way back to Stripe is a quiet link, because pressing it mints a
    // fresh single-use Account Link and session every time.
    // Owner, 2026-09-25: only the way back to Stripe renders here — the
    // "Stripe has everything it needs" sentence went, along with the section
    // that carried it.
    <div>
      <TWButton
        variant="link"
        size="sm"
        loading={linkLoading}
        disabled={statusLoading || statusUnknown}
        onClick={onClickContinueOnStripe}
        aria-label="Update your details on Stripe (opens in a new window)"
      >
        Update your details on Stripe
      </TWButton>
    </div>
  ) : (
    <div className="space-y-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-300">
        Stripe asks for your business details and bank account on its own secure
        page. It takes about five minutes, and you can come back here whenever
        you like.
      </p>
      <TWButton
        variant="primary"
        size="lg"
        icon={ArrowTopRightOnSquareIcon}
        loading={linkLoading}
        disabled={statusLoading || statusUnknown}
        onClick={onClickContinueOnStripe}
        aria-label="Continue on Stripe (opens in a new window)"
      >
        Continue on Stripe
      </TWButton>
    </div>
  );

  // After the hooks, never before them: SC-R8-DASH-01 mounts this component
  // where a company may not exist yet, and a button whose only action is
  // startOnboarding(undefined) must not be reachable there.
  if (!companyId) return null;

  return (
    <div className="space-y-6">
      <Notification
        classAttributes="pt-20"
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />

      <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-300">
        Your business and bank details are collected securely by our payment
        provider. Thisway never sees or stores this information.
      </p>

      {/* No Thisway-drawn progress rail here, ever: Stripe's embedded
          component runs its own multi-step progress and a second rail beside
          it would drift out of sync. Progress is Stripe's form's alone to
          show — this page draws nothing over it.

          There is no loading state to render between these two branches:
          nothing is minted until onClickContinueOnStripe resolves (its own
          `linkLoading` already covers that wait on the button itself), and
          turning a resolved secret into a mounted instance is synchronous
          (see use-connect-instance.js), so `showEmbedded` is accurate the
          instant `clientSecret` is set. */}
      {showEmbedded ? (
        // connect.js loads asynchronously after loadConnectAndInitialize has
        // already returned an instance, so a blocked script or a render throw
        // inside Stripe's components would otherwise leave an empty box with no
        // way forward — and escape past this route to the router's errorElement.
        <ErrorBoundary fallback={linkPath}>
          <div className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <ConnectComponentsProvider connectInstance={instance}>
                {/* Account onboarding only. Never mount ConnectPayments or
                    ConnectPaymentDetails: they expose Stripe's processing cost,
                    which a restaurant must never see. */}
                <ConnectAccountOnboarding
                  onExit={onExitOnboarding}
                  collectionOptions={collectionOptions}
                />
              </ConnectComponentsProvider>
            </div>
            <TWButton
              variant="link"
              size="sm"
              loading={linkLoading}
              disabled={statusLoading || statusUnknown}
              onClick={onClickHavingTrouble}
              aria-label="Having trouble? Continue on Stripe (opens in a new window)"
            >
              Having trouble? Continue on Stripe
            </TWButton>
          </div>
        </ErrorBoundary>
      ) : (
        linkPath
      )}

      {/* Owner, 2026-09-25: no checklist and no reassurance here — the page is
          Stripe's form. Only the two failure states stay, because each is a
          way back in: a refresh that failed, and a first read that failed. */}
      {refreshFailed && !statusLoading && !statusUnknown && (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {"Couldn't refresh your Stripe status just now."}
          </p>
          <TWButton variant="link" size="sm" onClick={loadStatus}>
            Try again
          </TWButton>
        </div>
      )}
      {!statusLoading && statusUnknown && (
        <div className="space-y-2">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {"We couldn't check your payout setup just now."}
          </p>
          <TWButton variant="link" size="sm" onClick={loadStatus}>
            Try again
          </TWButton>
        </div>
      )}
    </div>
  );
};

export default ConnectOnboarding;
